import axios from '@axios'

export const AUTH_SUBSCRIPTIONS_INCLUDE = 'abilities.limits,packages'
export const PUBLIC_SUBSCRIPTIONS_INCLUDE = 'abilities.limits,packages'

let subscriptionsRequest = null
let publicSubscriptionsRequest = null

const createCachedResponse = collection => ({
  data: {
    data: Array.isArray(collection) ? collection : [],
  },
})

const normalizeString = value => String(value || '')
  .toLowerCase()
  .trim()

const isTechnicalSubscription = subscription => {
  const slug = normalizeString(subscription && subscription.slug)

  return slug.includes('trial')
}

const extractCollection = response => {
  const payload = response && response.data ? response.data : response

  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload && payload.data)) return payload.data

  return []
}

const normalizeSubscriptionPackage = subscriptionPackage => {
  if (!subscriptionPackage || typeof subscriptionPackage !== 'object') return subscriptionPackage

  return {
    ...subscriptionPackage,
    price: subscriptionPackage.price !== undefined && subscriptionPackage.price !== null
      ? Number(subscriptionPackage.price)
      : 0,
    months: subscriptionPackage.months !== undefined && subscriptionPackage.months !== null
      ? Number(subscriptionPackage.months)
      : 0,
  }
}

const normalizeSubscription = subscription => {
  if (!subscription || typeof subscription !== 'object') return subscription

  return {
    ...subscription,
    packages: Array.isArray(subscription.packages)
      ? subscription.packages.map(normalizeSubscriptionPackage)
      : [],
    abilities: Array.isArray(subscription.abilities) ? subscription.abilities : [],
  }
}

const normalizeCollection = collection => (
  Array.isArray(collection)
    ? collection
      .filter(item => !isTechnicalSubscription(item))
      .map(normalizeSubscription)
    : []
)

const normalizePackageId = payload => {
  if (payload && typeof payload === 'object') {
    return payload.id || payload.package_id || payload.subscription_package_id || payload.subscriptionPackageId
  }

  return payload
}

const normalizeBuyPayload = payload => {
  if (!payload || typeof payload !== 'object') return {}

  const {
    id,
    package_id: packageId,
    subscription_package_id: subscriptionPackageId,
    subscriptionPackageId: camelPackageId,
    ...body
  } = payload

  return body
}

export default {
  namespaced: true,

  state: {
    subscriptions: [],
    publicSubscriptions: [],
    loading: false,
    publicLoading: false,
    error: null,
    publicError: null,
    buying: false,
    buyingError: null,
    subscriptionsFetched: false,
    publicSubscriptionsFetched: false,
  },

  getters: {
    subscriptions: state => state.subscriptions,
    publicSubscriptions: state => state.publicSubscriptions,
    loading: state => state.loading,
    publicLoading: state => state.publicLoading,
    error: state => state.error,
    publicError: state => state.publicError,
    buying: state => state.buying,
    buyingError: state => state.buyingError,
    subscriptionsFetched: state => state.subscriptionsFetched,
    publicSubscriptionsFetched: state => state.publicSubscriptionsFetched,
    availableSubscriptions: state => (
      state.subscriptions.length ? state.subscriptions : state.publicSubscriptions
    ),
  },

  mutations: {
    SET_SUBSCRIPTIONS(state, subscriptions) {
      state.subscriptions = normalizeCollection(subscriptions)
      state.subscriptionsFetched = true
    },

    SET_PUBLIC_SUBSCRIPTIONS(state, subscriptions) {
      state.publicSubscriptions = normalizeCollection(subscriptions)
      state.publicSubscriptionsFetched = true
    },

    SET_LOADING(state, value) {
      state.loading = value
    },

    SET_PUBLIC_LOADING(state, value) {
      state.publicLoading = value
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    SET_PUBLIC_ERROR(state, error) {
      state.publicError = error
    },

    SET_BUYING(state, value) {
      state.buying = value
    },

    SET_BUYING_ERROR(state, error) {
      state.buyingError = error
    },

    RESET_SUBSCRIPTIONS_CACHE(state) {
      state.subscriptionsFetched = false
      state.publicSubscriptionsFetched = false
      state.subscriptions = []
      state.publicSubscriptions = []
    },
  },

  actions: {
    async fetchSubscriptions({ commit, state }, payload = {}) {
      if (!payload.force && state.subscriptionsFetched) {
        return createCachedResponse(state.subscriptions)
      }

      if (subscriptionsRequest) return subscriptionsRequest

      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      subscriptionsRequest = axios.get('/public/subscriptions', {
        params: {
          include: payload.include || AUTH_SUBSCRIPTIONS_INCLUDE,
        },
      })

      try {
        const response = await subscriptionsRequest

        commit('SET_SUBSCRIPTIONS', extractCollection(response))

        return response
      } catch (error) {
        commit('SET_ERROR', error)
        throw error
      } finally {
        subscriptionsRequest = null
        commit('SET_LOADING', false)
      }
    },

    async fetchPublicSubscriptions({ commit, state }, payload = {}) {
      if (!payload.force && state.publicSubscriptionsFetched) {
        return createCachedResponse(state.publicSubscriptions)
      }

      if (publicSubscriptionsRequest) return publicSubscriptionsRequest

      commit('SET_PUBLIC_LOADING', true)
      commit('SET_PUBLIC_ERROR', null)

      publicSubscriptionsRequest = axios.get('/public/subscriptions', {
        params: {
          include: payload.include || PUBLIC_SUBSCRIPTIONS_INCLUDE,
        },
      })

      try {
        const response = await publicSubscriptionsRequest

        commit('SET_PUBLIC_SUBSCRIPTIONS', extractCollection(response))

        return response
      } catch (error) {
        commit('SET_PUBLIC_ERROR', error)
        throw error
      } finally {
        publicSubscriptionsRequest = null
        commit('SET_PUBLIC_LOADING', false)
      }
    },

    async fetchAvailableSubscriptions({ dispatch }, payload = {}) {
      if (payload.authenticated) {
        return dispatch('fetchSubscriptions', payload)
      }

      return dispatch('fetchPublicSubscriptions', payload)
    },

    async buySubscriptionPackage({ commit }, payload) {
      const packageId = normalizePackageId(payload)

      if (!packageId) {
        throw new Error('Subscription package id is required')
      }

      commit('SET_BUYING', true)
      commit('SET_BUYING_ERROR', null)

      try {
        return await axios.post(
          `/subscriptions/packages/${encodeURIComponent(packageId)}/buy`,
          normalizeBuyPayload(payload),
        )
      } catch (error) {
        commit('SET_BUYING_ERROR', error)
        throw error
      } finally {
        commit('SET_BUYING', false)
      }
    },

    buyPackageWithCredits({ dispatch }, payload) {
      return dispatch('buySubscriptionPackage', payload)
    },
  },
}
